import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../shared/service/http.service';
import { HubServiceBase } from 'src/app/shared/service/hub-service-base';
import { AuthService } from 'src/app/core/auth/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Notification, NotificationType } from 'src/app/shared/model/notification';
@Injectable({
  providedIn: 'root',
})
export class NotificationService extends HubServiceBase {
  private notificationsSource = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSource.asObservable();

  private notificationCountSource = new BehaviorSubject<number>(0);
  public notificationCount$ = this.notificationCountSource.asObservable();

  private userRoutes = environment.api.user;
  private notificationRoutes = environment.api.notification;
  private friendRoutes = environment.api.friendship;

  constructor(private apiService: ApiService, private auth: AuthService) {
    super();
    this.createConnection(environment.hub.notification, () => this.auth.accessToken);

  }

  public on(eventName: string, callback: (...args: any[]) => void): void {
    this.hubConnection?.on(eventName, callback);
  }

  public off(eventName: string): void {
    this.hubConnection?.off(eventName);
  }

  override start(): Promise<void> {
    this.hubConnection!.on('NewMessageNotification', (notification: Notification) => {      
      const normalized = this.normalizeNotification(notification);
      const updated = [normalized, ...this.notificationsSource.value];
      this.notificationsSource.next(updated);
      this.updateNotificationCount(this.getUnreadCount(updated));
    });

  this.hubConnection!.on("NotificationRead", (notificationId: number) => {
      const updated = this.notificationsSource.value.map((n) =>
          n.id === Number(notificationId) ? { ...n, isRead: true } : n,
      );
      this.notificationsSource.next(updated);
      this.updateNotificationCount(this.getUnreadCount(updated));
  });

    this.hubConnection!.on('RemoveNotification', (notificationId: number) => {
      const updated = this.notificationsSource.value.filter(n => n.id !== notificationId);
      this.notificationsSource.next(updated);
      this.updateNotificationCount(this.getUnreadCount(updated));
    });

    this.hubConnection!.on('NotificationsCounted', (count: number) => {
      this.notificationCountSource.next(count);
    });

    return super.start().then(() => {
    this.refreshNotifications();
  });
}


  private removeNotificationFromList(notificationId: number): void {
    const currentNotifications = this.notificationsSource.value;
    const updatedNotifications = currentNotifications.filter(n => n.id !== notificationId);

    if (updatedNotifications.length !== currentNotifications.length) {
        this.notificationsSource.next(updatedNotifications);
        this.updateNotificationCount(this.getUnreadCount(updatedNotifications));
    }
  }

  removeNotification(id: number) {
    const updatedNotifications = this.notificationsSource.value.filter((n) => n.id !== id);
    this.notificationsSource.next(updatedNotifications);
    this.updateNotificationCount(this.getUnreadCount(updatedNotifications));
  }
  deleteNotification(notificationId: number): Observable<{ response: boolean }> {
  const url = `${this.notificationRoutes.remove}?id=${notificationId}`;
  return this.apiService.Delete<{ response: boolean }>(url).pipe(
    tap((res) => {
      if (res.response) {
        // Update local state after backend confirms deletion
        this.removeNotificationFromList(notificationId);
      }
    }),
    catchError((err: HttpErrorResponse) => {
      console.error('Error deleting notification:', err);
      return throwError(() => err);
    })
  );
}

  decreaseNotificationCount(): void {
    const currentCount = this.notificationCountSource.value;
    if (currentCount > 0) {
      this.notificationCountSource.next(currentCount - 1); // Decrease notification count
    }
  }
  refreshNotifications(): void {
    this.apiService.Get<Notification[]>(this.userRoutes.getNotifications, { params: { onlyUnread: false } })
      .pipe(
        catchError(err => {
          console.error('Failed to load notifications', err);
          // Keep existing notifications if the refresh fails.
          return of(this.notificationsSource.value);
        })
      )
      .subscribe({
        next: (notifications) => {
          const validNotifications = Array.isArray(notifications)
            ? notifications.map((n) => this.normalizeNotification(n))
            : [];
          this.notificationsSource.next(validNotifications);
          this.updateNotificationCount(this.getUnreadCount(validNotifications));
        },
        error: (err) => {
          console.error('Notification stream error', err);
        }
      });
  }

  /** Ensure postId/targetObjectId are always present for navigation (SignalR + API). */
  private normalizeNotification(notification: Notification): Notification {
    const raw = notification as Notification & {
      Id?: number;
      IsRead?: boolean;
      Type?: NotificationType;
      Text?: string;
      Created?: Date | string;
      ActorId?: number;
      UserId?: number;
      Post?: any;
      Comment?: any;
      FriendshipRequest?: any;
    };

    //const postId = Number(raw.postId ?? raw.PostId ?? 0);
    //const targetObjectId = Number(raw.targetObjectId ?? raw.TargetObjectId ?? 0);
   // const resolvedPostId = postId > 0 ? postId : targetObjectId > 0 ? targetObjectId : undefined;
    const created = this.toUtcDate(raw.created ?? raw.Created);

    return {
      ...raw,
      id: Number(raw.id ?? raw.Id ?? 0),
      //postId: resolvedPostId,
      //targetObjectId: targetObjectId > 0 ? targetObjectId : resolvedPostId ?? 0,
      isRead: Boolean(raw.isRead ?? raw.IsRead),
      type: (raw.type ?? raw.Type) as NotificationType,
      text: raw.text ?? raw.Text ?? '',
      created,
      actorId: Number(raw.actorId ?? raw.ActorId ?? 0),
      userId: Number(raw.userId ?? raw.UserId ?? 0),
      post: raw.post ?? raw.Post ?? null,
      comment: raw.comment ?? raw.Comment ?? null,
      friendshipRequest: raw.friendshipRequest ?? raw.FriendshipRequest ?? null,
    } as Notification;
  }

  private toUtcDate(value: Date | string | undefined): Date {
    if (value instanceof Date) {
      return new Date(value);
    }

    if (typeof value === 'string') {
      const hasTimezone = /(?:[zZ]|[+-]\d{2}:\d{2})$/.test(value);
      const date = new Date(hasTimezone ? value : `${value}Z`);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }

    return new Date();
  }

  getNotifications(): Observable<Notification[]> {
    return this.notifications$;
  }

  getNotificationCount(): Observable<number> {
    return this.notificationCount$;
  }

  markNotificationAsRead(notificationId: number): Observable<{ response: boolean }> {
    return this.apiService
      .Get<{ response: boolean }>(this.notificationRoutes.markAsRead, { params: { id: notificationId } })
      .pipe(
        tap((res) => {
          if (res.response) {
            const updatedNotifications = this.notificationsSource.value.map((n) => {
              if (n.id === Number(notificationId)) {
                return { ...n, isRead: true }; // Mark as read
              }
              return n;
            });


            this.notificationsSource.next(updatedNotifications);
            this.updateNotificationCount(this.getUnreadCount(updatedNotifications));
          }
        })
      );
  }

  private getUnreadCount(notifications: Notification[]): number {
    return notifications.filter(n => n && !n.isRead).length;
  }

  private updateNotificationCount(count: number) {
    this.notificationCountSource.next(count);
  }

    updateNotifications(notifications: Notification[]): void {
      this.notificationsSource.next(notifications);
      this.updateNotificationCount(this.getUnreadCount(notifications));
    }



// Pause notifications for a specific post (with userId fetched from the backend)
//pauseNotificationsForPost(postId: number): Observable<any> {
//  return this.apiService.Post(`${environment.api.notification.setPauseState}?postId=${postId}&isPaused=true`, {});
//}

// notification.service.ts
setPauseState(postId: number, isPaused: boolean): Observable<{ success: boolean }> {
  const url = `${environment.api.notification.setPauseState}`;
  const body = { postId, isPaused };
  return this.apiService.Post<{ success: boolean }>(url, body);
}

// Update the pause state for notifications
updatePauseState(postId: number, isPaused: boolean): Observable<any> {
  return this.apiService.Post(
    `${environment.api.notification.setPauseState}?postId=${postId}&isPaused=${isPaused}`, {}
  );
}

addComment(comment: { userId: number; postId: number; content: string }): Observable<any> {
  return this.apiService.Post('/api/comment/AddComment', comment).pipe(
    catchError((err) => {
      if (err.error?.message) {
        alert(err.error.message);
      } else {
        alert("Failed to post comment.");
      }
      return throwError(() => err);
    })
  );
}

}
